import { useState, useMemo } from "react";
import { useGrowerAssignment, type EntitySortOption } from "../shared";
import type { BranchConfigurationProps } from "./BranchConfiguration.types";

export function useBranchConfigurationViewModel(props: BranchConfigurationProps) {
	const { jobs, branches, regions, branchAssignments, onAssignGrowerToBranch } = props;

	const shared = useGrowerAssignment({
		jobs,
		entities: branches,
		assignments: branchAssignments,
		onAssign: onAssignGrowerToBranch,
	});

	// Build branchId → region name lookup
	const regionByBranchId = useMemo(() => {
		const map = new Map<string, string>();
		if (regions) {
			for (const region of regions) {
				if (region.branchIds) {
					for (const branchId of region.branchIds) {
						map.set(branchId, region.name);
					}
				}
			}
		}
		return map;
	}, [regions]);

	// Branch sort & search state
	const [branchSortOption, setBranchSortOption] = useState<EntitySortOption>("default");
	const [searchText, setSearchText] = useState("");

	const filteredSortedBranches = useMemo(() => {
		const trimmed = searchText.trim().toLowerCase();
		let result = branches;

		if (trimmed) {
			result = result.filter((branch) => {
				if (branch.name.toLowerCase().includes(trimmed)) return true;
				const region = regionByBranchId.get(branch.id);
				if (region && region.toLowerCase().includes(trimmed)) return true;
				return false;
			});
		}

		if (branchSortOption === "default") return result;

		return [...result].sort((a, b) => {
			switch (branchSortOption) {
				case "name-asc":
					return a.name.localeCompare(b.name);
				case "name-desc":
					return b.name.localeCompare(a.name);
				case "growers-desc": {
					const aCount = (shared.growersByEntity.get(a.id) || []).length;
					const bCount = (shared.growersByEntity.get(b.id) || []).length;
					return bCount - aCount;
				}
				case "growers-asc": {
					const aCount = (shared.growersByEntity.get(a.id) || []).length;
					const bCount = (shared.growersByEntity.get(b.id) || []).length;
					return aCount - bCount;
				}
				default:
					return 0;
			}
		});
	}, [branches, branchSortOption, shared.growersByEntity, searchText, regionByBranchId]);

	return {
		...shared,
		branchSortOption,
		setBranchSortOption,
		searchText,
		setSearchText,
		filteredSortedBranches,
		totalBranchCount: branches.length,
		regionByBranchId,
	};
}
